// ============================================
// Base Types
// ============================================

export interface ProductMeta {
  views: number;
  purchases: number;
}

export interface VariantInfo {
  name: string;
  sku: string;
  price: number;
  quantity: number;
  weight: number;
  weightUnit: string;
  imageUrl?: string;
  requiresShipping: boolean;
  taxable: boolean;
  trackInventory: boolean;
  isDefault: boolean;
}

// ============================================
// Product Related DTOs
// ============================================

export interface Product {
  id: string;
  _id?: string;
  name?: string;
  title?: string;
  slug?: string;
  description?: string;
  price?: number;
  compareAtPrice?: number | null;
  quantity?: number;
  weight?: number;
  weightUnit?: string;
  categories?: string[] | any[]; // Can be array of strings or objects with name/title
  variants?: VariantInfo[];
  images?: string[];
  image?: string;
  imageUrl?: string;
  isActive?: boolean;
  isFeatured?: boolean;
  hasVariants?: boolean;
  requiresShipping?: boolean;
  taxable?: boolean;
  tags?: string[];
  trackInventory?: boolean;
  inventoryPolicy?: string;
  reviewCount?: number;
  ratingsQuantity?: number;
  rating?: number;
  sku?: string;
  meta?: ProductMeta;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  variantTitle?: string;
  variantImage?: string;
  variantSku?: string;
}

// ============================================
// Cart Product DTOs
// ============================================

export interface CartProduct {
  productId: string;
  slug?: string;
  variantSku?: string;
  variantId?: string;
  variantTitle?: string;
  variantImage?: string;
  quantity: number;
  variantInfo?: VariantInfo;
  description?: string;
  imageUrl?: string;
  name?: string;
  price?: number;
  categoryId?: string;
}

// ============================================
// Handler Options DTOs
// ============================================

export interface AddToCartOptions {
  quantity?: number;
  variantSku?: string;
  imageUrl?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export interface AddToCartClickOptions extends AddToCartOptions {
  cartLoading?: boolean;
  preventNavigation?: boolean;
}

// ============================================
// Batch Operation DTOs
// ============================================

export interface BatchAddItem {
  product: Product;
  quantity?: number;
  variantSku?: string;
}

export interface BatchAddResult {
  success: number;
  failed: number;
  errors: Array<{
    product: Product;
    error: any;
  }>;
}

// ============================================
// Function Types
// ============================================

export type AddToCartFunction = (
  product: Product,
  quantity?: number,
  variantSku?: string
) => Promise<void>;

// ============================================
// Constants
// ============================================

export const CART_CONSTANTS = {
  MAX_QUANTITY: 99,
  MIN_QUANTITY: 1,
  DEFAULT_QUANTITY: 1,
  DEFAULT_WEIGHT_UNIT: 'g',
  DEFAULT_INVENTORY_POLICY: 'deny',
  MAX_ITEMS: 999,
} as const;